import type React from 'react'
import type { InputHTMLAttributes } from 'react'
import { cn } from '../lib/cva'
import {
	type BaseFormElementProps,
	type FormElementStyleProps,
	FormElementWrapper,
	formStyles,
	useFormElementState,
} from './FormElementWrapper'

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'disabled'>, BaseFormElementProps {
	formClassName?: string
}

export const Input: React.FC<InputProps> = ({
	label,
	hint,
	showHintIcon = false,
	variant = 'default',
	disabled = false,
	className,
	value: controlledValue,
	defaultValue,
	onChange,
	isRequired = false,
	onBlur,
	type,
	formClassName,
	...props
}) => {
	const {
		value,
		handleChange,
		handleFocus,
		handleBlur: wrapperBlur,
		styleProps,
	} = useFormElementState<HTMLInputElement>({
		value: controlledValue,
		defaultValue,
		variant,
		disabled,
		onChange,
	})

	const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		wrapperBlur()
		onBlur?.(e)
	}

	const renderInput = (inputStyleProps?: FormElementStyleProps) => {
		const mergedStyleProps = inputStyleProps || styleProps
		return (
			<input
				className={cn(formStyles.element(mergedStyleProps), className)}
				disabled={disabled}
				value={value}
				onChange={handleChange}
				onFocus={handleFocus}
				onBlur={handleBlur}
				type={type}
				{...props}
			/>
		)
	}

	return (
		<FormElementWrapper
			label={label}
			hint={hint}
			showHintIcon={showHintIcon}
			variant={variant}
			disabled={disabled}
			isRequired={isRequired}
			styleProps={styleProps}
			className={formClassName}>
			{renderInput()}
		</FormElementWrapper>
	)
}
